// Considered: TreeMapSwimlane — rejected because it is a one-dimensional sphere lane bound to
// TreeMap data. Considered: ComposerCanvasOrganism — rejected because it is a free-positioning
// composition host. Neither is a fixed stage x lane matrix whose cells are drop targets. See spec
// Reuse Audit (arc-editor-canvas-v1.md).
import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'localization';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import { UNATTRIBUTED_LANE_KEY } from '../../../utils/arc/arcCanvasModel';
import './ArcLaneGridMolecule.scss';

export interface ArcLaneGridStage {
  id: string;
}

/** A perspective lane (`rows`, `arc-app.jsx:577`) — the system lane, the consequence lane, and the
 * synthesized Unattributed lane are structural and rendered by this component, not data-driven. */
export interface ArcLaneGridLane {
  id: string;
  /** True when this lane is backed by neither the universal schema nor an applicable schema —
   * only an intent names it (`resolveLanesForModel`'s `ArcLaneDefinition.pending`). Passed through
   * unopinionated to `renderLaneLabel` so the host can forward it to `ArcLaneLabelMolecule`. */
  pending?: boolean;
}

export interface ArcLaneGridMoleculeProps {
  stages: ArcLaneGridStage[];
  /** Perspective lanes only — excludes the system lane, Unattributed, and the consequence lane. */
  lanes: ArcLaneGridLane[];
  /** Disables the add-stage column (`locked` — a read-only arc has no add affordances). */
  editable?: boolean;
  renderStageHeader: (stage: ArcLaneGridStage, index: number) => React.ReactNode;
  renderSystemLaneLabel: () => React.ReactNode;
  renderSystemCell: (stage: ArcLaneGridStage) => React.ReactNode;
  renderLaneLabel: (lane: ArcLaneGridLane) => React.ReactNode;
  renderLaneCell: (stage: ArcLaneGridStage, lane: ArcLaneGridLane) => React.ReactNode;
  renderUnattributedLabel: () => React.ReactNode;
  renderUnattributedCell: (stage: ArcLaneGridStage) => React.ReactNode;
  /**
   * The consequence lane under the perspectives (what a stage leads to). The row is
   * rendered only when a caller supplies it: an arc with no consequence source has no
   * empty band at the foot of the grid.
   */
  renderConsequenceLabel?: () => React.ReactNode;
  renderConsequenceCell?: (stage: ArcLaneGridStage) => React.ReactNode;
  onAddStage: () => void;
  /**
   * Fires when a palette tile is dropped on a lane cell or the Unattributed cell (`laneKey`
   * matches `ArcLaneGridLane.id`, or `UNATTRIBUTED_LANE_KEY`). System/consequence cells
   * are not drop targets — they carry no `dropTargetProps`. The host owns interpreting which
   * tile was dragged (its own `draggingId`) and dispatching the corresponding model op.
   */
  onCellDrop?: (laneKey: string, stageId: string) => void;
  /**
   * Enter/Space on a stage's column-head cell (spec keyboard map: "Enter on a head/card selects
   * it"). The grid owns the roving tabindex, so the head cell — not the card inside it — is the
   * focus stop; the card's own click is the pointer path to the same selection.
   */
  onStageActivate?: (stage: ArcLaneGridStage) => void;
  /** Escape on a cell (spec keyboard map: "Escape clears selection"). Escape inside a cell's
   * widget returns focus to the cell instead. */
  onClearSelection?: () => void;
  className?: string;
}

const FOCUSABLE_IN_CELL = 'button:not([disabled]), input:not([disabled]), a[href], [tabindex="0"]';

/** The canvas region: stages across, lanes down (`arc-app.jsx:546-604`). Cell content is supplied
 * by the caller via render props — this component owns only grid structure and the corner /
 * add-stage affordances. */
const ArcLaneGridMolecule: React.FC<ArcLaneGridMoleculeProps> = ({
  stages,
  lanes,
  editable = false,
  renderStageHeader,
  renderSystemLaneLabel,
  renderSystemCell,
  renderLaneLabel,
  renderLaneCell,
  renderUnattributedLabel,
  renderUnattributedCell,
  renderConsequenceLabel,
  renderConsequenceCell,
  onAddStage,
  onCellDrop,
  onStageActivate,
  onClearSelection,
  className = '',
}) => {
  const { t } = useTranslation();
  const [overCellId, setOverCellId] = useState<string | null>(null);

  // Roving tabindex (WAI-ARIA grid pattern): one cell is the grid's tab stop; ←→↑↓ move it;
  // Enter steps into the cell's first widget (or selects a stage head); Escape steps back out.
  // Rows: 0 heads, 1 system, 2… lanes, then Unattributed, then the consequence lane when
  // supplied. Cols: 0 the row head, 1…n the stages, n+1 the add column (heads row only).
  const [activeCell, setActiveCell] = useState('0:0');
  const cellRefs = useRef(new Map<string, HTMLDivElement>());
  const stageCount = stages.length;
  const hasConsequence = Boolean(renderConsequenceLabel && renderConsequenceCell);
  const rowCount = 3 + lanes.length + (hasConsequence ? 1 : 0);
  const lastCol = useCallback(
    (row: number) => (row === 0 && editable ? stageCount + 1 : stageCount),
    [editable, stageCount]
  );
  const cellKey = (row: number, col: number) => `${row}:${col}`;
  const focusCell = useCallback((row: number, col: number) => {
    const key = `${row}:${col}`;
    setActiveCell(key);
    cellRefs.current.get(key)?.focus();
  }, []);
  const cellProps = (row: number, col: number) => {
    const key = cellKey(row, col);
    return {
      ref: (el: HTMLDivElement | null) => {
        if (el) cellRefs.current.set(key, el);
        else cellRefs.current.delete(key);
      },
      tabIndex: activeCell === key ? 0 : -1,
      'data-grid-cell': key,
      // Focus landing anywhere in the cell (a widget reached by Tab, a pointer click) makes it
      // the tab stop, so leaving and re-entering the grid resumes where the person was.
      onFocus: () => setActiveCell(key),
    };
  };
  const handleGridKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      const cellEl = target.closest<HTMLElement>('[data-grid-cell]');
      const key = cellEl?.dataset.gridCell;
      if (!cellEl || !key) return;
      const [row, col] = key.split(':').map(Number);
      const onCell = target === cellEl;
      if (e.key === 'Escape') {
        e.preventDefault();
        if (onCell) onClearSelection?.();
        else cellEl.focus();
        return;
      }
      // Arrows and Enter inside a widget (a name input, a button) belong to the widget.
      if (!onCell) return;
      switch (e.key) {
        case 'ArrowRight':
          focusCell(row, Math.min(col + 1, lastCol(row)));
          break;
        case 'ArrowLeft':
          focusCell(row, Math.max(col - 1, 0));
          break;
        case 'ArrowDown': {
          const next = Math.min(row + 1, rowCount - 1);
          focusCell(next, Math.min(col, lastCol(next)));
          break;
        }
        case 'ArrowUp': {
          const next = Math.max(row - 1, 0);
          focusCell(next, Math.min(col, lastCol(next)));
          break;
        }
        case 'Home':
          focusCell(row, 0);
          break;
        case 'End':
          focusCell(row, lastCol(row));
          break;
        case 'Enter':
        case ' ':
          if (row === 0 && col >= 1 && col <= stageCount) {
            onStageActivate?.(stages[col - 1]);
          } else {
            cellEl.querySelector<HTMLElement>(FOCUSABLE_IN_CELL)?.focus();
          }
          break;
        default:
          return;
      }
      e.preventDefault();
    },
    [stages, stageCount, rowCount, lastCol, focusCell, onStageActivate, onClearSelection]
  );
  const unattributedRow = 2 + lanes.length;
  const consequenceRow = unattributedRow + 1;
  const gridTemplateColumns = `var(--arc-lane-grid-label-width) repeat(${stages.length}, minmax(var(--arc-lane-grid-col-min), 1fr))${
    editable ? ' var(--arc-lane-grid-addcol-width)' : ''
  }`;

  const dropTargetProps = (cellId: string, laneKey: string, stageId: string) =>
    editable
      ? {
          onDragOver: (e: React.DragEvent) => {
            e.preventDefault();
            setOverCellId(cellId);
          },
          onDragLeave: () => setOverCellId((prev) => (prev === cellId ? null : prev)),
          onDrop: (e: React.DragEvent) => {
            e.preventDefault();
            setOverCellId(null);
            onCellDrop?.(laneKey, stageId);
          },
          'data-over': overCellId === cellId ? 'true' : undefined,
        }
      : {};

  return (
    <div className={`arc-lane-grid ${className}`.trim()} data-region="canvas">
      <div
        aria-live="polite"
        className="arc-lane-grid__dropstatus"
        data-testid="arc-lane-grid-dropstatus"
      >
        {overCellId ? t('studio.arcEditor.grid.dropHere') : ''}
      </div>
      <div className="arc-lane-grid__scroll" data-container="group" tabIndex={0}>
        <div
          className="arc-lane-grid__grid"
          role="grid"
          aria-label={t('studio.arcEditor.grid.ariaLabel')}
          data-testid="arc-lane-grid"
          data-container="group > table:arc-canvas-stages-across-lanes-down"
          style={{ gridTemplateColumns }}
          onKeyDown={handleGridKeyDown}
        >
          <div className="arc-lane-grid__row" role="row">
            <div
              className="arc-lane-grid__corner"
              role="columnheader"
              data-container="group:stages"
              {...cellProps(0, 0)}
            >
              <span className="arc-lane-grid__corner-label" data-datum="canvas.stages" data-kind="label">
                {t('studio.arcEditor.grid.cornerLabel')}
              </span>
            </div>
            {stages.map((stage, index) => (
              <div
                key={stage.id}
                className="arc-lane-grid__stage-head"
                role="columnheader"
                data-container="each(cols)"
                {...cellProps(0, index + 1)}
              >
                {renderStageHeader(stage, index)}
              </div>
            ))}
            {editable && (
              <div className="arc-lane-grid__addcol" role="columnheader" {...cellProps(0, stageCount + 1)}>
                <ButtonAtom
                  variant="dashed"
                  icon="pi pi-plus"
                  label={t('studio.arcEditor.grid.addStage')}
                  onClick={onAddStage}
                  className="arc-lane-grid__add-stage"
                  data-datum="canvas.add-stage"
                  data-kind="button"
                  data-testid="arc-lane-grid-add-stage"
                />
              </div>
            )}
          </div>

          <div className="arc-lane-grid__row" role="row" data-testid="arc-lane-grid-system-lane">
            <div className="arc-lane-grid__rowhead" role="rowheader" {...cellProps(1, 0)}>
              {renderSystemLaneLabel()}
            </div>
            {stages.map((stage, index) => (
              <div
                key={stage.id}
                className="arc-lane-grid__cell"
                role="gridcell"
                data-container="each(cols)"
                {...cellProps(1, index + 1)}
              >
                {renderSystemCell(stage)}
              </div>
            ))}
            {editable && <div className="arc-lane-grid__addcol" role="gridcell" />}
          </div>

          {lanes.map((lane, laneIndex) => (
            <div
              key={lane.id}
              className="arc-lane-grid__row"
              role="row"
              data-testid={`arc-lane-grid-lane-${lane.id}`}
              data-container="each(rows)"
            >
              <div className="arc-lane-grid__rowhead" role="rowheader" {...cellProps(2 + laneIndex, 0)}>
                {renderLaneLabel(lane)}
              </div>
              {stages.map((stage, index) => (
                <div
                  key={stage.id}
                  className="arc-lane-grid__cell"
                  role="gridcell"
                  data-container="each(cols)"
                  data-testid={`arc-lane-grid-cell-${lane.id}-${stage.id}`}
                  {...cellProps(2 + laneIndex, index + 1)}
                  {...dropTargetProps(`${lane.id}::${stage.id}`, lane.id, stage.id)}
                >
                  {renderLaneCell(stage, lane)}
                </div>
              ))}
              {editable && <div className="arc-lane-grid__addcol" role="gridcell" />}
            </div>
          ))}

          <div className="arc-lane-grid__row" role="row" data-testid="arc-lane-grid-unattributed-lane">
            <div className="arc-lane-grid__rowhead" role="rowheader" {...cellProps(unattributedRow, 0)}>
              {renderUnattributedLabel()}
            </div>
            {stages.map((stage, index) => (
              <div
                key={stage.id}
                className="arc-lane-grid__cell"
                role="gridcell"
                data-testid={`arc-lane-grid-cell-unattributed-${stage.id}`}
                {...cellProps(unattributedRow, index + 1)}
                {...dropTargetProps(`unattributed::${stage.id}`, UNATTRIBUTED_LANE_KEY, stage.id)}
              >
                {renderUnattributedCell(stage)}
              </div>
            ))}
            {editable && <div className="arc-lane-grid__addcol" role="gridcell" />}
          </div>

          {renderConsequenceLabel && renderConsequenceCell && (
            <div
              className="arc-lane-grid__row arc-lane-grid__row--consequence"
              role="row"
              data-testid="arc-lane-grid-consequence-lane"
            >
              <div className="arc-lane-grid__rowhead" role="rowheader" {...cellProps(consequenceRow, 0)}>
                {renderConsequenceLabel()}
              </div>
              {stages.map((stage, index) => (
                <div
                  key={stage.id}
                  className="arc-lane-grid__cell"
                  role="gridcell"
                  {...cellProps(consequenceRow, index + 1)}
                >
                  {renderConsequenceCell(stage)}
                </div>
              ))}
              {editable && <div className="arc-lane-grid__addcol" role="gridcell" />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

ArcLaneGridMolecule.displayName = 'ArcLaneGridMolecule';

export default ArcLaneGridMolecule;
